import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useShallow } from 'zustand/react/shallow'
import cx from "classnames";
import Button from "../../components/Button";
import Dependent from "../../models/Dependent";
import Member from "../../models/Member";
import Agent from "../../models/Agent";
import Plan from "../../models/Plan";
import {
  dateIsValid,
  formatDate,
  formatDateString,
  getFirstDayOfMonth,
  getMaxDateBilling,
  getMaxDateEffective,
  getMinDateBilling,
  isBillingCurrent,
} from "../../utils";
import BillingDateModal from "../BillingDateModal";
import { Tooltip } from "@material-tailwind/react";
import DatePicker from "react-datepicker";
import { createPortal } from "react-dom";
import { PlanLabel } from "../../enums";
import useApplicationStore from "../../context/application";
import metlifeDentalData from "../../metlifeJsonData";
import * as PlanIds from '../../planIds';

type ConfirmationProps = {
  agent: Agent;
  member: Member;
  selectedPlans: Plan[];
  associatedPlan: Plan;
  dependents: Dependent[];
  wantsDental: boolean;
  setMember: React.Dispatch<React.SetStateAction<Member>>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setSelectedPlans: React.Dispatch<React.SetStateAction<Plan[]>>;
};

const Confirmation: FC<ConfirmationProps> = ({
  member,
  agent,
  selectedPlans,
  associatedPlan,
  dependents,
  wantsDental,
  setMember,
  setStep,
  setSelectedPlans,
}: ConfirmationProps) => {
  // TODO why set another dependents state?
  const [dependentsArray, setDependentsArray] =
    useState<Dependent[]>(dependents);
  const [totalValue, setTotalValue] = useState(0);
  const [validEffectiveDates, setValidEffectiveDates] = useState<Date[]>();
  // Define plan-specific effective date rules
  const planEffectiveDateXMonthsOut: { [planId: number]: number } = {
    [PlanIds.NCDSmile1000byMetLife]: 1, // Smile plans only have 1 month effective date
    [PlanIds.NCDSmile2500byMetLife]: 1  // Smile plans only have 1 month effective date
  };
  
  // Determine months to add with proper priority
  const getMonthsToAdd = () => {
    const selectedPlanId = selectedPlans[0]?.id;
    
    // Priority 1: Check if selected plan has a specific rule
    if (selectedPlanId && planEffectiveDateXMonthsOut[selectedPlanId] !== undefined) {
      return planEffectiveDateXMonthsOut[selectedPlanId];
    }
    
    // Priority 2: Check agent feature flags
    if (agent.featureFlags?.effectiveDateXMonthsOut !== undefined) {
      return agent.featureFlags.effectiveDateXMonthsOut;
    }
    
    // Priority 3: Default value
    return 2;
  };

  const [monthsToAdd, setMonthsToAdd] = useState(getMonthsToAdd())
  const [loading, setLoading] = useState(true)
  const [errorDate, setErrorDate] = useState("");

  // billingDate as Date as opposed to member.billingDate which is a formatted string
  const [billingDate, setBillingDate] = useState<Date>(
    member.billingDate ? new Date(member.billingDate) : new Date()
  );

  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [applicationDate, setApplicationDate] = useApplicationStore(useShallow((state) => [
    state.applicationDate,
    state.setApplicationDate,
  ]));

  const maxBillingDate: Date = useMemo(() => new Date(getMaxDateBilling(monthsToAdd)),[monthsToAdd])
  const minBillingDate: Date = useMemo(() => new Date(getMinDateBilling()),[])
  const maxEffectiveDate: Date = useMemo(() => new Date(getMaxDateEffective(monthsToAdd)), [monthsToAdd]);
  const effectiveDateDescription: String = useMemo(() => {
    const part1 = "Effective date must be the first of the month following enrollment"
    const part2 = `or the first of ${monthsToAdd > 2 ? `one of the following ${monthsToAdd - 1}` : 'the following'} month${monthsToAdd > 2 ? 's' : ''}.`
    return monthsToAdd < 2 ? `${part1}.` : `${part1} ${part2}`
  }, [monthsToAdd])

  // Update monthsToAdd when agent feature flags or selected plans change
  useEffect(() => {
    setMonthsToAdd(getMonthsToAdd());
    if(agent.featureFlags) {
      setLoading(false)
    }
  }, [agent.featureFlags, selectedPlans])
  
  /* sets member.effectiveDate to the 1st available validEffectiveDate, when member.effectiveDate is not set*/
  useEffect(() => {
    if(loading) {return}

    if (validEffectiveDates && validEffectiveDates[0] && (!member.effectiveDate)) {
      setMember(prevState => ({
        ...prevState,
        effectiveDate: formatDate(validEffectiveDates[0])
      }));
    }
  }, [validEffectiveDates, loading, setMember]);

  // initial set billingDate
  useEffect(() => {
    let initialBillingDate: string = formatDate(billingDate);
    if (member.billingDate && dateIsValid(member.billingDate)) {
      initialBillingDate = formatDateString(
        formatDate(new Date(member.billingDate))
      );
    }

    setMember(prevState => ({...prevState, billingDate: initialBillingDate}))
  }, []);

  const validBillingDate = (chosenDate: Date) => {
    if (chosenDate < minBillingDate) {
      setErrorDate("Date can't be before today");
      return false;
    }
    // doing this date to string to date to compare year, month day. without hours. UTC date coming from the datepicker was ahead for some reason
    if (new Date(chosenDate.toDateString()) > new Date(maxBillingDate.toDateString())) {
      setErrorDate(
        `Date can't be further than ${monthsToAdd} months from today or the same as Effective Date`
      );
      // setValidEffectiveDates([chosenDate]);
      return false;
    }

    return true;
  }
  const handleChangeBillingDate = useCallback((chosenDate: Date) => {
    if(!validBillingDate(chosenDate)) {
      return false;
    }

    setMember(prevState => ({...prevState, billingDate: formatDate(chosenDate)}));
    setBillingDate(chosenDate);
    // setValidEffectiveDates(newValidDates);
    setErrorDate("");
  },[monthsToAdd, setMember, maxBillingDate, minBillingDate]);

  const calculateValidEffectiveDates = (billingDate: Date, monthsToAdd: number) => {
    const newValidEffectiveDates: Date[] = []

    // bases validEffectiveDates on billingDate, and then filters if <= lastValidMonth
    for (let i = 1; i <= monthsToAdd; i++) {
      const month = billingDate.getMonth() + i; // ex 1
      const aValidDate = getFirstDayOfMonth(billingDate.getFullYear(), month);
      if (aValidDate <= maxEffectiveDate && aValidDate > billingDate) {
        newValidEffectiveDates.push(aValidDate);
      }
    }

    return newValidEffectiveDates;
  }

  // initial setting of effectiveDate
  // also runs upon billingDate and effectiveDate update
  useEffect(() => {
    // wait for agent login to process...
    if(loading) {
      return;
    }

    setDependentsArray(dependents);

    const newValidEffectiveDates = calculateValidEffectiveDates(billingDate, monthsToAdd)
    if (newValidEffectiveDates.length === 0) {
      console.warn('No valid dates!', newValidEffectiveDates)
      setErrorDate(
        `Date can't be further than ${monthsToAdd} months from today or the same as the effective date`
      );
      // setValidEffectiveDates([billingDate]);
      return;
    } else {
      setValidEffectiveDates(newValidEffectiveDates);
    }

    const formattedValidDates = newValidEffectiveDates.map((date) =>
      formatDate(date)
    );

    // this forces effectiveDate to be selected, and one of the formattedValidDates
    if (
      member.effectiveDate && !formattedValidDates.includes(member.effectiveDate)
    ) {
      setMember(prevState => ({
        ...prevState,
        effectiveDate: formattedValidDates[0]
      }));
    }
  }, [billingDate, monthsToAdd, member.effectiveDate, loading, dependents, setMember]);

  useEffect(() => {
    setDependentsArray(dependents);
    const value = calculateTotalValue(selectedPlans, associatedPlan)
    setTotalValue(value);
  }, [selectedPlans, associatedPlan, dependents]);

  {/*Final Overview Price Issue: If new products are added, this useEffect needs to be edited to include the added plans to prevent the price from being displayed incorrectly on the confirmation page*/}
  useEffect(() => {
    if(loading) {
      return;
    }

    if (
      selectedPlans[0].label !== PlanLabel.MetLifeElite1500 &&
      selectedPlans[0].label !== PlanLabel.MetLifeElite3000 &&
      selectedPlans[0].label !== PlanLabel.MetLifeElite5000 &&
      selectedPlans[0].label !== PlanLabel.NCDSmile1000byMetLife &&
      selectedPlans[0].label !== PlanLabel.NCDSmile2500byMetLife &&
      selectedPlans[0].label !== PlanLabel.NCDBright1000byMetLife &&
      selectedPlans[0].label !== PlanLabel.NCDBright1500byMetLife &&
      wantsDental
    ) {
      const tempArray = [...selectedPlans];
      if (metlifeDentalData[selectedPlans[0].index || 0].PriceUpdated !== undefined) {
        tempArray[0].rate =
          metlifeDentalData[selectedPlans[0].index || 0].PriceUpdated;
      } else {
        tempArray[0].rate =
          metlifeDentalData[selectedPlans[0].index || 0].Price;
      }
      setSelectedPlans(tempArray);
    }
  }, [applicationDate, loading]);

  const calculateTotalValue = (selectedPlans: Plan[], associatedPlan: Plan) => {
    let value = 0;
    const plans = [...selectedPlans, associatedPlan];
    plans.forEach((plan, index) => {
      if (plan?.rate && plan?.id) {
        if (index === plans.length - 1 && plan.rateUpdate !== undefined && plan.rateUpdate > 0) {
          if (plan?.rateUpdate) value += plan.rateUpdate;
        } else {
          value += plan.rate;
        }
      }
    });
    return value;
  }

  const planLogoHandler = () => {
    switch (selectedPlans[0]?.label) {
      case PlanLabel.NCDValueByMetLife:
        return "/ncd-value.svg";
      case PlanLabel.NCDEssentialsByMetLife:
        return "/ncd-essentials.svg";
      case PlanLabel.NCDCompleteByMetLife:
        return "/ncd-complete.svg";
      case PlanLabel.MetLifeElite1500:
        return "/ncd-elite-1500.svg";
      case PlanLabel.MetLifeElite3000:
        return "/ncd-elite-3000.svg";
      case PlanLabel.MetLifeElite5000:
        return "/ncd-elite-5000.svg";
      case PlanLabel.NCDSmile1000byMetLife:
        return "/ncd-smile-1000.svg";
      case PlanLabel.NCDSmile2500byMetLife:
        return "/ncd-smile-2500.svg";
      case PlanLabel.NCDBright1000byMetLife:
        return "/bright-1000.svg";
      case PlanLabel.NCDBright1500byMetLife:
        return "/bright-1500.svg";
      default:
        return "/ncd-value.svg";
    }
  };

  return (
    <div
      className={cx(
        "flex min-h-[70vh] w-full flex-col",
        "px-2 py-2",
        "xl:pt-17"
      )}
    >
      <div className="page-header-container">
        <h1 className="page-header-text">Overview</h1>
      </div>
      <div
        className={cx(
          "flex w-full flex-col",
          "rounded-3xl border-4",
          "border-black bg-white",
          "p-4",
          "xl:px-16 xl:pt-12"
        )}
      >
        <div
          className={cx(
            "grid grid-cols-1 gap-4",
            "text-xl",
            "md:grid-cols-4 md:gap-y-4 md:gap-x-20"
          )}
        >
          <div className="flex flex-col xl:max-w-[260px]">
            <div className="font-bold text-celadonBlue">Member</div>
            <div>{member.firstName}</div>
            <div> {member.lastName}</div>
          </div>
          <div className="flex flex-col xl:max-w-[260px]">
            <div className="font-bold text-celadonBlue">Date of Birth</div>
            <div>{member.dateOfBirth}</div>
          </div>
          <div className="flex flex-col xl:max-w-[260px]">
            <div className="font-bold text-celadonBlue">Gender</div>
            <div>{member.gender === "M" ? "Male" : "Female"}</div>
          </div>
          <div className="flex flex-col xl:max-w-[260px]">
            <div className="font-bold text-celadonBlue">Address</div>

            <div className="flex-col">{member.address1 + ","}</div>
            <div className="flex-col">{member.city + ","}</div>
            <div className="flex-col">
              {member.zipCode} {member.state}
            </div>
          </div>
          {dependentsArray.length > 0 && (
            <div className="my-2 block h-[2px] w-full bg-navyBlue md:hidden" />
          )}
          {dependentsArray?.map((dependent, i) => {
            return (
              <>
                <div className="flex flex-col xl:max-w-[260px]" key={i}>
                  <div className="font-bold text-celadonBlue">
                    {dependent.relationship}
                  </div>
                  <div>{dependent.firstName}</div>
                  <div> {dependent.lastName}</div>
                </div>
                <div className="flex flex-col xl:max-w-[260px]">
                  <div className="font-bold text-celadonBlue">
                    Date of Birth
                  </div>
                  <div>{dependent.dateOfBirth}</div>
                </div>
                <div className="flex flex-col xl:max-w-[260px]">
                  <div className="font-bold text-celadonBlue">Gender</div>
                  <div>{dependent.gender === "M" ? "Male" : "Female"}</div>
                </div>

                <div className="hidden flex-col md:flex">
                  <div className="hidden font-bold text-celadonBlue">
                    Address
                  </div>
                </div>

                {i !== dependentsArray.length - 1 && (
                  <div className="my-2 block h-[2px] w-full bg-navyBlue md:hidden" />
                )}
              </>
            );
          })}
        </div>
        <div className="bottom-[5.5rem] z-[50] flex w-full flex-col bg-white pb-2">
          <div className="my-4 h-[2px] w-full bg-navyBlue" />

          <div
            className={cx(
              "flex-col items-center justify-center",
              "gap-[40px]",
              "xl:flex xl:flex-row xl:items-start"
            )}
          >
            <div
              className={cx(
                "flex items-center justify-between",
                "mb-4 gap-4",
                "xl:mb-0 xl:flex-col xl:items-start xl:gap-0"
              )}
            >
              <div className="text-2xl font-bold" data-cy="confirmationTotal">Total Monthly Cost</div>
              <div className="text-2xl font-bold text-navyBlue">
                ${totalValue}/mo
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col self-start">
                <label className="mb-[11px] flex flex-row items-center text-xl font-bold text-navyBlue">
                  Product Effective Date{" "}
                  <Tooltip
                    content={effectiveDateDescription}
                    className="z-50 bg-black bg-opacity-75 p-2"
                  >
                    <img
                      className="ml-1 h-5 w-5"
                      src="/question_mark_icon.svg"
                      alt=""
                    />
                  </Tooltip>
                </label>
                <select
                  className={`form-input border-celadonBlue bg-white xl:w-[262px] ${missingFields.includes("effectiveDate") && "border-red"
                    }`}
                  onChange={(event) => {
                    setMember({...member, effectiveDate: event.target.value});
                    setApplicationDate(new Date(event.target.value).getMonth());
                  }}
                  value={member.effectiveDate}
                  required
                  data-cy="effectiveDate"
                >
                  {(validEffectiveDates === undefined || validEffectiveDates?.length === 0) &&
                    <option value={member.effectiveDate || ""} key={99}>
                      {member.effectiveDate ? new Date(member.effectiveDate).toDateString() : "Select Date"}
                    </option>
                  }
                  {validEffectiveDates?.map((date, i) => {
                    if (formatDate(date) === member.effectiveDate) {
                      return (
                        <option
                          className={date <= billingDate ? `hidden` : ``}
                          disabled={date <= billingDate}
                          value={formatDate(date)}
                          key={i}
                          // selected={date <= billingDate ? false : true}
                        >
                          {date.toDateString()}
                        </option>
                      );
                    }
                    return (
                      <option
                        key={i}
                        className={date <= billingDate ? `hidden` : ``}
                        disabled={date <= billingDate}
                        value={formatDate(date)}
                      >
                        {date.toDateString()}
                      </option>
                    );
                  })}
                </select>
                {missingFields.includes("effectiveDate") && (
                  <h2 className="error">This field is required.</h2>
                )}
              </div>
              <div className="flex flex-col self-start">
                <label className="mb-[11px] flex flex-row items-center text-xl font-bold text-navyBlue xl:mt-0">
                  First Billing Date
                  <Tooltip
                    content="First billing date must be before the effective date."
                    className="z-50 bg-black bg-opacity-75 p-2"
                  >
                    <img
                      className="ml-1 h-5 w-5"
                      src="/question_mark_icon.svg"
                      alt=""
                    />
                  </Tooltip>
                </label>
                <DatePicker
                  className={`form-input w-full border-celadonBlue xl:w-[262px] ${missingFields.includes("billingDate") && "border-red"
                    }`}
                  onChange={(event) => {
                    if(event){
                      handleChangeBillingDate(event)
                    }
                  }}
                  value={
                    member.billingDate && dateIsValid(member.billingDate)
                      ? formatDate(new Date(member.billingDate))
                      : formatDate(new Date())
                  }
                  placeholderText="MM/DD/YYYY"
                  popperContainer={({ children }) =>
                    createPortal(children, document.body)
                  }
                  dateFormat="MM-dd-yyyy"
                  minDate={minBillingDate}
                  maxDate={maxBillingDate}
                  selected={billingDate}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  customInput={<input data-cy='billingDate' type='text' />}
                />
                {errorDate && <h2 className="error w-72">{errorDate}</h2>}
                {missingFields.includes("billingDate") && (
                  <h2 className="error">This field is required.</h2>
                )}
              </div>
            </div>
            <div className="mt-4 h-[52px] w-full font-inter text-2xl font-normal xl:mt-auto xl:ml-16 xl:w-[176px]">
              <Button
                text={"Confirm"}
                className="confirmPlan"
                action={() => {
                  if (errorDate === "") {
                    setStep((current) => current + 1);
                  }
                }}
                mainPath
                disabled={loading}
                loadingIcon={loading}
              />
            </div>
          </div>
        </div>
        <div className={cx("grid grid-cols-1 md:grid-cols-3", "gap-6 pt-4")}>
          {[...selectedPlans, associatedPlan]?.map((plan, index) => {
            if (!plan?.id) return null;
            return (
              <div className="flex gap-3" key={index}>
                <div className="w-[100px]">
                  <img
                    className={cx({
                      "mx-auto h-[45px] w-[45px]": index === 2,
                      "mx-auto h-[45px] w-[90px]": index !== 2,
                      "mx-auto": index === 0,
                    })}
                    src={
                      index === 0
                        ? planLogoHandler()
                        : index === 1
                          ? "/vsp.png"
                          : "/orange-plus.png"
                    }
                    alt=""
                  />
                </div>

                <div>
                  <div className="max-w-[150px] text-base font-normal">
                    {plan.label}
                  </div>
                  <div className="text-base font-bold text-navyBlue" data-cy={`finalPrice ${plan.label}`}>
                    $
                    {plan.rateUpdate !== undefined && index === 2
                      ? plan.rateUpdate
                      : plan.rate}
                    /mo
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
